import { createContext, useContext, type ReactNode } from 'react';
import type { AgentConnection, Scope, VaultSDK } from '@cef-ai/vault-sdk';
import type { CereWallet } from '@/lib/clients/cere-wallet/connect';
import type { VaultHandle, VaultRecord } from '@/lib/vault/types';
import type { AgentCard } from '@/src/data/agent-cards';

export interface VaultSession {
  wallet: CereWallet;
  vault: VaultRecord;
  /**
   * READ handle — bound to the Bearer read SDK. Safe to use from an effect or
   * a timer, because it never signs.
   */
  vaultHandle: VaultHandle;
  /**
   * SIGNING handle — bound to the wallet-signing SDK, resolved LAZILY.
   *
   * Not a plain field, because obtaining it costs a signed `vault.current()`
   * round-trip, and doing that at boot is exactly what the Bearer decision
   * exists to prevent: after a reload the wallet is keyless (`resume()`
   * restores the bearer session, never the PRF signing keys), so a signed
   * boot read raises `needs-step-up` and its popup is blocked for want of a
   * user gesture. Every caller of this is user-initiated — agents.connect,
   * scopes.create/update/delete, agents.get behind a dialog — so the ceremony,
   * if one is needed, is paid for by a real click. Memoized per session.
   */
  signingHandle: () => Promise<VaultHandle>;
  sdk: VaultSDK;
  agents: AgentConnection[];
  agentCards: Record<string, AgentCard>;
  /**
   * False until the initial agents/scopes fetch resolves. Lets surfaces
   * distinguish "still loading" from "loaded but empty" (e.g. the
   * dashboard shows a loader instead of the empty-state pitch during the
   * first roundtrip). Optional — undefined is treated as already-loaded
   * so older test fixtures don't need updating.
   */
  agentsLoaded?: boolean;
  scopes: Scope[];
  vaultBaseUrl: string;
  vaultDisplayName: string;
  walletDisplayName?: string;
  disconnect: () => void;
  /**
   * Whether the vault's write-delegation grant (the on-chain NonTransfer
   * proxy to vault-api, granted during the claim) is known to be registered.
   *
   * Deliberately NOT derived from `vault.status`. That is the credential's
   * status — 'active' whenever vault-api minted a working credential during
   * `ensure` — and on a resumed session it says nothing about whether this
   * device knows the proxy is in place. Conflating them made Settings assert
   * renewal was enabled while it was not.
   *
   * `undefined` means unknown (legacy stored record, or a vault claimed on
   * another device): render as unconfirmed, but do not warn — there is no
   * known failure to report. A fresh claim always sets `true` (a grant
   * failure throws out of `ensure` and fails the claim).
   */
  delegateGranted: boolean | undefined;
  /** Update the delegate/connected state (vault.status) — the single source of
   *  truth shared by the Settings storage control and the disconnected banner.
   *  Called after a successful Settings- or banner-side re-authorize. */
  setDelegateActive: (active: boolean) => void;
  refreshScopes: () => Promise<void>;
  refreshAgents: () => Promise<void>;
}

const VaultSessionContext = createContext<VaultSession | null>(null);

export function VaultSessionProvider({ value, children }: { value: VaultSession; children: ReactNode }) {
  return <VaultSessionContext.Provider value={value}>{children}</VaultSessionContext.Provider>;
}

export function useVaultSession(): VaultSession {
  const ctx = useContext(VaultSessionContext);
  if (!ctx) {
    throw new Error('useVaultSession must be used inside <VaultSessionProvider>');
  }
  return ctx;
}
